/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import {Router} from '@angular/router';
import {StickyHeaderService} from '../../shared/sticky-header/sticky-header.service';
import {UserService} from '../../shared/services/user.service';
import {Permission} from '../../shared/utils/permission.enum';
import {StickyHeaderIconButtonComponent} from '../../shared/sticky-header/html-elements/sticky-header-icon-button.component';
import {SpecialOrderService} from '../../api/ms-specialorders/specialorder/specialorder.service';
import {Subscription} from 'rxjs';

import * as XLSX from 'xlsx';
import moment from 'moment';
import {SpecialOrderReportQueryModel} from '../../api/ms-specialorders/contracts/special-order-report-query.model';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ConfirmModalComponent} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.component';
import {AlertNotificationSeverity} from '../../alert-notification-severity.model';
import {ConfirmModal} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.model';
import {SortCriterionModel} from '../../api/generic/contracts/sort-criterion.model';
import { HttpErrorResponse } from '@angular/common/http';
import {SpecialOrderMaterialDeliveryReportDetailModel} from '../../api/ms-specialorders/contracts/special-order-material-delivery-report-detail.model';
import {SpecialOrderQueryModel} from '../../api/ms-specialorders/contracts/special-order-query.model';

type WorksheetData = any[][];

@Component({
    selector: 'app-spo-main-page',
    templateUrl: './spo-main-page.component.html',
    styleUrls: ['./spo-main-page.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class SpoMainPageComponent implements OnInit, OnDestroy {

    public contextActions: StickyHeaderIconButtonComponent[] = [];
    public reportCriteria: SpecialOrderReportQueryModel;
    public subscriptions = new Subscription();

    private readonly fileName: string = 'orcan-specialorders-report.xlsx';
    private readonly worksheetHeader: (string | number)[] = [
        'Partner No.',
        'Partner Description',
        'SPO Number',
        'Sent Date',
        'Sent Time',
        'Materials',
        'Reason Code',
        'Reason Description',
        'Changed on Date',
        'Changed on Time',
        'Requested Date',
        'Ship to Party Code',
        'Ship to Party Description',
        'Supplying Packing Plant Code',
        'Supplying Packing Plant Description',
        'Transport Mode Code',
        'Transport Mode Description',
        'Transport Service Code',
        'Transport Service Description',
        'Justification',
        'Sales No.',
        'Material No.',
        'Material Description',
        'Ordered QTY',
        'Model Type',
        'LOT No.',
        'MCDR No.',
        'IGEF No.',
        'Colour Code',
        'Total Sent QTY',
        'Total Open QTY',
        'UoM',
        'Status',
        'Transport No.',
        'QTY in Shipment',
        'AWB/BoL',
        'Handling Unit',
        'Est. Arrival'
    ];
    private readonly exportAction: StickyHeaderIconButtonComponent = {
        text: 'Export',
        icon: 'iwp-icon-gen_export_excel',
        click: (): void => {
            this.getSpecialOrderReport({
                searchCriteria: this.reportCriteria.searchCriteria,
                sortCriteria: this.reportCriteria.sortCriteria
            });
        },
        id: 'sticky-header-export-excel-button',
        class: 'btn-text-link',
        dataCy: 'sticky-header-export-excel-button',
        tooltip: 'Export to excel'
    };

    private readonly EXCEL_DATE_FORMAT: string = 'DD-MM-YYYY';
    private readonly EXCEL_TIME_FORMAT: string = 'HH:mm:ss';

    constructor(private router: Router,
                private stickyHeaderService: StickyHeaderService,
                private userService: UserService,
                private spoService: SpecialOrderService,
                private modalService: NgbModal) {
    }

    ngOnInit(): void {
        setTimeout(() => {
            if (this.userService.hasPermissions([Permission.PARTNER_SPO_CREATE])) {
                this.stickyHeaderService.registerMainAction({
                    text: 'New',
                    click: (): void => {
                        this.router.navigate(['/', 'spo', 'new']);
                    },
                    id: 'sticky-header-new-button',
                    class: 'btn-primary',
                    dataCy: 'sticky-header-new-button',
                    tooltip: 'Create new SPO'
                });
            }

            if (this.userService.hasPermissions([
                Permission.PARTNER_SPO_EXPORT,
                Permission.ADMIN_SPO_EXPORT
            ], 'any')) {
                this.contextActions.push(this.exportAction);
            }

            this.stickyHeaderService.registerContextActions(this.contextActions);
        });
    }

    ngOnDestroy(): void {
        this.stickyHeaderService.clearActions();
    }

    /**
     * Sets the search and sort criteria for the SPO report
     * @param query Object containing the searchCriteria {@link SpecialOrderQueryModel}
     * {@link SortCriterionModel}from the special order main page list results
     */
    setReportCriteria(query: SpecialOrderQueryModel): void {
        this.reportCriteria = query == null ? null : {
            searchCriteria: query.searchCriteria,
            sortCriteria: query.paginationCriteria == null ? null : query.paginationCriteria.sortCriteria
        };
    }

    /**
     * Gets the special order report response from the BE
     * @param queryModel the search and sort criteria for the query
     * Calls {@link export}
     * Opens warning modal if exceed-records-number error is returned
     */
    getSpecialOrderReport(queryModel: SpecialOrderReportQueryModel): void {
        this.exportAction.loading = this.exportAction.disabled = true;
        this.stickyHeaderService.registerContextActions(this.contextActions);

        this.subscriptions.add(this.spoService.getSpecialOrdersReport(queryModel)
                .subscribe((value) => {
                            this.export(value.deliveries);
                        },
                        (error: HttpErrorResponse) => {
                            const confirmModalInstance: ConfirmModalComponent = this.modalService
                                    .open(ConfirmModalComponent, {centered: true}).componentInstance;
                            confirmModalInstance.initModal({
                                titleText: 'Warning',
                                subtitleText: error.error?.message,
                                alertNotificationSeverity: AlertNotificationSeverity.WARNING,
                                secondaryButtonText: 'Close'
                            } as ConfirmModal);
                        })
                .add(() => {
                    this.exportAction.loading = this.exportAction.disabled = false;
                    this.stickyHeaderService.registerContextActions(this.contextActions);
                })
        );
    }

    mapWorksheetData(deliveries: SpecialOrderMaterialDeliveryReportDetailModel[]): WorksheetData {
        let worksheetData: WorksheetData = [];
        if (deliveries != null) {
            worksheetData = deliveries.map(delivery => [
                delivery.partner != null ? delivery.partner.code : null,
                delivery.partner != null ? delivery.partner.name : null,
                delivery.spoNumber,
                delivery.sentDate != null ? {
                    v: moment(delivery.sentDate)
                            .toDate(),
                    t: 'd',
                    z: this.EXCEL_DATE_FORMAT
                } : null,
                delivery.sentDate != null ? {
                    v: moment(delivery.sentDate)
                            .toDate(),
                    t: 'd',
                    z: this.EXCEL_TIME_FORMAT
                } : null,
                delivery.numberOfMaterials,
                delivery.reason != null ? delivery.reason.code : null,
                delivery.reason != null ? delivery.reason.name : null,
                // Based of off: https://stackoverflow.com/q/41342929
                // Cell Object documentation: https://docs.sheetjs.com/#user-content-cell-object
                delivery.changedOn != null ? {
                    v: moment(delivery.changedOn)
                            .toDate(),
                    t: 'd',
                    // Key 'z' is the number format string associated with the cell
                    z: this.EXCEL_DATE_FORMAT
                } : null,
                delivery.changedOn != null ? {
                    v: moment(delivery.changedOn)
                            .toDate(),
                    t: 'd',
                    z: this.EXCEL_TIME_FORMAT
                } : null,
                delivery.requestedDate != null ? {
                    v: moment.utc(delivery.requestedDate)
                            // Since Excel always shows dates in local time and we want to show an absolute date to the user,
                            // we need to "hack" the date/moment object by subtracting from it the local date offset.
                            .subtract(moment(delivery.requestedDate)
                                    .utcOffset(), 'minutes')
                            .toDate(),
                    t: 'd',
                    z: this.EXCEL_DATE_FORMAT
                } : null,
                delivery.shipToParty != null ? delivery.shipToParty.code : null,
                delivery.shipToParty != null ? delivery.shipToParty.name : null,
                delivery.supplyingPackingPlant != null ? delivery.supplyingPackingPlant.code : null,
                delivery.supplyingPackingPlant != null ? delivery.supplyingPackingPlant.name : null,
                delivery.transportMode != null ? delivery.transportMode.code : null,
                delivery.transportMode != null ? delivery.transportMode.name : null,
                delivery.transportService != null ? delivery.transportService.code : null,
                delivery.transportService != null ? delivery.transportService.name : null,
                delivery.justification,
                delivery.salesOrderNumber,
                delivery.material != null ? delivery.material.number : null,
                delivery.material != null ? delivery.material.description : null,
                delivery.totalOrderedQuantity,
                delivery.modelType,
                delivery.lotNumber,
                delivery.mcdrNumber,
                delivery.igefNumber,
                delivery.colourCode,
                delivery.totalSentQuantity,
                delivery.totalOpenQuantity,
                delivery.material != null ? delivery.material.salesUnit : null,
                delivery.materialStatus != null ? delivery.materialStatus.description : null,
                delivery.transportNumber,
                delivery.sentQuantity,
                delivery.transportDocumentNumber,
                delivery.handlingUnit,
                delivery.estimatedTimeArrival != null ? {
                    v: moment.utc(delivery.estimatedTimeArrival)
                            // Since Excel always shows dates in local time and we want to show an absolute date to the user,
                            // we need to "hack" the date/moment object by subtracting from it the local date offset.
                            .subtract(moment(delivery.estimatedTimeArrival)
                                    .utcOffset(), 'minutes')
                            .toDate(),
                    t: 'd',
                    z: this.EXCEL_DATE_FORMAT
                } : null
            ]);
        }
        const worksheet = [this.worksheetHeader].concat(worksheetData);

        return worksheet;
    }

    export(deliveries: SpecialOrderMaterialDeliveryReportDetailModel[]): void {
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(this.mapWorksheetData(deliveries), {skipHeader: true});

        // Format the date/time columns, so they don't show up as ######
        worksheet['!cols'] = new Array(this.worksheetHeader.length);
        worksheet['!cols'][6] = {wch: 10.5};
        worksheet['!cols'][7] = {wch: 8.5};
        worksheet['!cols'][8] = {wch: 10.5};
        worksheet['!cols'][33] = {wch: 10.5};

        XLSX.utils.book_append_sheet(workbook, worksheet, 'SPO Report');

        // > The default behavior for all parsers is to generate number cells.
        // > Setting cellDates to true will force the generators to store dates.
        // https://docs.sheetjs.com/#user-content-dates
        XLSX.writeFile(workbook, this.fileName, {cellDates: true});
    }
}
